import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output,
  SimpleChanges, ViewChild
} from '@angular/core';
import { Subscription } from 'rxjs';
import { CHAT_STRINGS_PT, ChatStrings } from '../../chat.strings';
import {
  ChatPresenceMap, ChatPresenceStatus, ChatSupervisionFilter, ChatSupervisionGroupRow, ChatSupervisionKind, ChatUser
} from '../../chat.model';
import { ChatService } from '../../services/chat.service';

/** One consultant's block (or "Sem responsável") in "Supervisão". */
export interface ChatSupervisionGroupView {
  key: string;
  /** null = "Sem responsável" (`GET /chat/supervision/none/chats`). */
  userAppId: string | null;
  name: string;
  photo?: string;
  total: number;
  waiting: number;
  rows: ChatUser[];
  open: boolean;
  loaded: boolean;
  loadingRows: boolean;
  failedRows: boolean;
}

/** Consultants per page and conversations per page of a block (the server caps both). */
const GROUP_STEP = 10;
const ROW_STEP = 20;
/** "Por atribuir" comes whole from `GET /chat/queue`; it is shown 20 at a time. */
const QUEUE_STEP = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The "Supervisão" view of the conversation list (2026-10-27).
 *
 * Before, `GET /chat/users` brought every team conversation at once; a MASTER
 * of a network with 500 users loaded them all on opening the chat. Now:
 *  - the consultants come 10 at a time (`GET /chat/supervision`), **closed**,
 *    with "N conversas · M à espera"; the next 10 load when the end of the list
 *    scrolls into view (a sentinel watched by an IntersectionObserver, with a
 *    "Carregar mais" button as fallback);
 *  - opening a block loads its conversations, 20 at a time
 *    (`GET /chat/supervision/:userAppId/chats`, `none` for "Sem responsável");
 *  - `kind` is the footer tab (Clientes | Entre consultores), `filter` the
 *    chips (Todas | À espera de resposta, clients only) and `search` goes to
 *    the server (debounced); any of them starts over from the first page;
 *  - "Por atribuir" (the queue, already loaded by the container) stays first,
 *    closed as well, in Clientes.
 * Rows are read-only (`supervised`): opening one is the container's
 * `getConversation`, as for any row.
 */
@Component({
  selector: 'chat-supervision',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './chat-supervision.component.html',
  styleUrls: ['./chat-supervision.component.css']
})
export class ChatSupervisionComponent implements OnChanges, OnDestroy {
  @Input() kind: ChatSupervisionKind = 'clients';
  @Input() filter: ChatSupervisionFilter = 'all';
  @Input() search = '';
  /** `GET /chat/queue`, already filtered by the search (container). */
  @Input() queue: ChatUser[] = [];
  /** canAssign: the "Por atribuir" block exists (Clientes only). */
  @Input() canAssign = false;
  @Input() activeKey: string | null = null;
  @Input() presence: ChatPresenceMap = {};
  @Input() online: string[] = [];
  /** Base of the public profile photos (`/users/files/`). */
  @Input() profileBase = '';
  /** The container's row normalisation (photo, time, unread), so rows look like the list's. */
  @Input() normalizeRow: (row: any) => ChatUser = (row: any) => row;
  @Input() strings: ChatStrings = CHAT_STRINGS_PT;
  @Output() select = new EventEmitter<ChatUser>();

  groups: ChatSupervisionGroupView[] = [];
  total = 0;
  loading = false;
  loadingMore = false;
  failed = false;
  queueOpen = false;
  queueLimit = QUEUE_STEP;
  readonly skeletonRows = [64, 52, 72, 58, 46];

  private observer?: IntersectionObserver;
  private sentinelEl?: HTMLElement;
  private pageSub?: Subscription;
  private rowSubs = new Map<string, Subscription>();
  private searchTimer: any = null;
  /** Bumped on every restart: answers of an older query are ignored. */
  private generation = 0;

  constructor(
    private chatService: ChatService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  @ViewChild('sentinel') set sentinel(ref: ElementRef<HTMLElement> | undefined) {
    const el = ref ? ref.nativeElement : undefined;
    if (el === this.sentinelEl) {
      return;
    }
    if (this.observer && this.sentinelEl) {
      this.observer.unobserve(this.sentinelEl);
    }
    this.sentinelEl = el;
    if (el) {
      this.observe(el);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const restart = !!changes['kind'] || !!changes['filter'];
    if (changes['search'] && !restart && !changes['search'].firstChange) {
      this.debouncedRestart();
      return;
    }
    if (restart) {
      this.restart();
    }
  }

  ngOnDestroy(): void {
    this.clearSearchTimer();
    this.cancelAll();
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  get hasMore(): boolean {
    return this.groups.length < this.total;
  }

  get showQueue(): boolean {
    return this.canAssign && this.kind === 'clients' && (this.queue || []).length > 0;
  }

  get visibleQueue(): ChatUser[] {
    return (this.queue || []).slice(0, this.queueLimit);
  }

  get isEmpty(): boolean {
    return !this.loading && !this.failed && this.groups.length === 0 && !this.showQueue;
  }

  retry(): void {
    this.restart();
  }

  loadMore(): void {
    if (this.hasMore && !this.loading && !this.loadingMore) {
      this.loadPage(this.groups.length);
    }
  }

  toggle(group: ChatSupervisionGroupView): void {
    group.open = !group.open;
    if (group.open && !group.loaded && !group.loadingRows) {
      this.loadRows(group, 0);
    }
  }

  moreRows(group: ChatSupervisionGroupView): void {
    if (!group.loadingRows) {
      this.loadRows(group, group.rows.length);
    }
  }

  hasMoreRows(group: ChatSupervisionGroupView): boolean {
    return group.loaded && group.rows.length < group.total;
  }

  toggleQueue(): void {
    this.queueOpen = !this.queueOpen;
  }

  moreQueue(): void {
    this.queueLimit += QUEUE_STEP;
  }

  summary(group: ChatSupervisionGroupView): string {
    const total = this.strings.groupConversations.replace('{n}', String(group.total));
    return this.kind === 'clients' && group.waiting > 0
      ? total + ' · ' + this.strings.groupWaiting.replace('{n}', String(group.waiting))
      : total;
  }

  queueSummary(): string {
    return this.strings.groupConversations.replace('{n}', String((this.queue || []).length));
  }

  presenceOf(c: ChatUser): ChatPresenceStatus | null {
    return c.userId ? this.presenceOfId(c.userId) : null;
  }

  presenceOfId(id?: string | null): ChatPresenceStatus | null {
    if (!id) {
      return null;
    }
    const known = this.presence[id];
    if (known) {
      return known;
    }
    return this.online.indexOf(id) > -1 ? 'online' : null;
  }

  keyOf(c: ChatUser): string {
    return c.chat ? 'chat:' + c.chat : 'user:' + (c.userId || '') + ':' + (c.propertyReference || c.propertyId || '');
  }

  trackByKey = (_i: number, c: ChatUser) => this.keyOf(c);
  trackByGroup = (_i: number, g: ChatSupervisionGroupView) => g.key;

  private debouncedRestart(): void {
    this.clearSearchTimer();
    this.searchTimer = setTimeout(() => {
      this.searchTimer = null;
      this.restart();
    }, SEARCH_DEBOUNCE_MS);
  }

  private restart(): void {
    this.clearSearchTimer();
    this.cancelAll();
    this.generation += 1;
    this.groups = [];
    this.total = 0;
    this.failed = false;
    this.queueLimit = QUEUE_STEP;
    this.loadPage(0);
  }

  private loadPage(offset: number): void {
    const generation = this.generation;
    if (offset === 0) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.cdr.markForCheck();
    if (this.pageSub) {
      this.pageSub.unsubscribe();
    }
    this.pageSub = this.chatService.getSupervisionGroups({
      kind: this.kind, filter: this.filter, search: (this.search || '').trim(), offset, limit: GROUP_STEP
    }).subscribe({
      next: page => {
        if (generation !== this.generation) {
          return;
        }
        const fresh = (page && Array.isArray(page.groups) ? page.groups : []).map(g => this.toGroup(g));
        this.groups = offset === 0 ? fresh : [...this.groups, ...fresh];
        this.total = page && page.total > 0 ? page.total : this.groups.length;
        this.loading = false;
        this.loadingMore = false;
        this.cdr.markForCheck();
        // A short page may leave the sentinel in view: look again once it is drawn.
        setTimeout(() => this.recheckSentinel());
      },
      error: () => {
        if (generation !== this.generation) {
          return;
        }
        this.loading = false;
        this.loadingMore = false;
        this.failed = offset === 0;
        this.cdr.markForCheck();
      }
    });
  }

  private loadRows(group: ChatSupervisionGroupView, offset: number): void {
    const generation = this.generation;
    group.loadingRows = true;
    group.failedRows = false;
    this.cdr.markForCheck();
    const previous = this.rowSubs.get(group.key);
    if (previous) {
      previous.unsubscribe();
    }
    const sub = this.chatService.getSupervisionChats(group.userAppId || 'none', {
      kind: this.kind, filter: this.filter, search: (this.search || '').trim(), offset, limit: ROW_STEP
    }).subscribe({
      next: rows => {
        if (generation !== this.generation) {
          return;
        }
        const list = (rows || []).filter(r => !!r).map(r => this.normalizeRow(r));
        group.rows = offset === 0 ? list : [...group.rows, ...list];
        group.loaded = true;
        group.loadingRows = false;
        // The count can change between the two requests: stop when a page comes short.
        if (list.length < ROW_STEP) {
          group.total = group.rows.length;
        }
        this.cdr.markForCheck();
      },
      error: () => {
        if (generation !== this.generation) {
          return;
        }
        group.loadingRows = false;
        group.failedRows = true;
        this.cdr.markForCheck();
      }
    });
    this.rowSubs.set(group.key, sub);
  }

  private toGroup(row: ChatSupervisionGroupRow): ChatSupervisionGroupView {
    const id = row.userAppId ? String(row.userAppId) : null;
    return {
      key: id || 'none',
      userAppId: id,
      name: row.name || (id ? '' : this.strings.noOwnerGroup),
      photo: row.image ? (/^(https?:)?\//.test(row.image) ? row.image : this.profileBase + row.image) : undefined,
      total: row.total > 0 ? row.total : 0,
      waiting: row.waiting > 0 ? row.waiting : 0,
      rows: [],
      open: false,
      loaded: false,
      loadingRows: false,
      failedRows: false
    };
  }

  private observe(el: HTMLElement): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    if (!this.observer) {
      this.observer = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) {
          this.zone.run(() => this.loadMore());
        }
      });
    }
    this.observer.observe(el);
  }

  /** Re-observing makes the observer report the current state (a sentinel already in view). */
  private recheckSentinel(): void {
    if (this.observer && this.sentinelEl) {
      this.observer.unobserve(this.sentinelEl);
      this.observer.observe(this.sentinelEl);
    }
  }

  private cancelAll(): void {
    if (this.pageSub) {
      this.pageSub.unsubscribe();
      this.pageSub = undefined;
    }
    this.rowSubs.forEach(sub => sub.unsubscribe());
    this.rowSubs.clear();
  }

  private clearSearchTimer(): void {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  }
}
